import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS, PHONE_NO_LENGTH, INPUT_MAX_25, TEXTAREA_MAX_LENGTH, languageSpoken, EXPERTISE, INPUT_LENGTH_40, INPUT_LENGTH_50, GENDER, ASTROLOGY_SYSTEMS } from "../../../../utils/Constants";
import { handleServerValidations, DT, globalLoader, blockInvalidChar, capitalizeFirstLetterOfEachWord } from "../../../../utils/commonfunction";
import * as Yup from "yup";
import CustomError from "../../../../utils/customError";
import { useTranslation } from "react-i18next";
import { Details, Edit } from "../../services/psychic.services";
import { List as ListCategories } from "../../services/pujaCategory.services";
import { TagPicker, Tooltip, Whisper, DatePicker } from "rsuite";
import PhoneInput from "../../../../utils/PhoneInput";
import { COMMON_INPUT_VALIDATION, EMAIL_VALIDATION, PHONE_VALIDATION } from "../../../../utils/commonValidations";

const extractLangCode = (lang) => {
    if (!lang) return "";
    let code = "";
    if (typeof lang === "object" && lang !== null) {
        code = lang.code || lang.language_name || lang.name || "";
    } else {
        code = String(lang).trim();
    }
    if (code.toLowerCase() === "[object object]") return "";
    const match = languageSpoken.find(
        (l) => l.code.toLowerCase() === code.toLowerCase() || l.name.toLowerCase() === code.toLowerCase()
    );
    return match ? match.code : code;
};

const extractLanguageCodes = (languagesInput) => {
    if (!languagesInput) return [];
    let list = Array.isArray(languagesInput) ? languagesInput : [];
    if (typeof languagesInput === "string") {
        try {
            list = JSON.parse(languagesInput);
        } catch (e) {
            list = languagesInput.split(",");
        }
    }
    return list
        .map((item) => extractLangCode(item))
        .filter(Boolean);
};

const extractSystemValues = (systemInput) => {
    if (!systemInput) return [];
    let list = Array.isArray(systemInput) ? systemInput : [];
    if (typeof systemInput === "string") {
        try {
            const parsed = JSON.parse(systemInput);
            if (Array.isArray(parsed)) {
                list = parsed;
            } else {
                list = systemInput.split(",");
            }
        } catch (e) {
            list = systemInput.split(",");
        }
    }
    return list
        .map((item) => {
            const str = String(item).trim();
            const match = ASTROLOGY_SYSTEMS.find(
                (sys) => sys.toLowerCase() === str.toLowerCase()
            );
            return match || str;
        })
        .filter(Boolean);
};

const systemData = ASTROLOGY_SYSTEMS.map((sys) => ({ label: sys, value: sys }));

const AstrologerEdit = (props) => {
    const navigate = useNavigate();
    const params = useParams();
    const { t } = useTranslation();
    const [showdefault, setShowDefault] = useState({});
    const [previewimage, setPreviewImage] = useState("");
    const [saveType, setSaveType] = useState("");
    const [categories, setCategories] = useState([]);

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        {
            title: t("Astrologer"),
            url: "/admin/astrologer-management/astrologer/list/1",
        },
        { title: t("Edit"), url: "" },
    ];

    useEffect(() => {
        // Fetch categories for dropdown
        ListCategories({ per_page: 100 })
            .then((res) => {
                if (res.data && res.data.list) {
                    setCategories(res.data.list);
                }
            })
            .catch(err => console.log("Category error:", err));

        Details(params.id)
            .then((response) => {
                setShowDefault(response && response.data ? response.data : []);
                setPreviewImage(response?.data?.profile_image);
            })
            .catch((error) => {
                console.log("error=====>", error);
            });
    }, [params.id]);

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .trim()
            .max(INPUT_LENGTH_40, DT(t("max_length_error_dynamic"), [INPUT_LENGTH_40]))
            .concat(COMMON_INPUT_VALIDATION)
            .required("Name is required"),

        screen_name: Yup.string()
            .trim()
            .max(INPUT_LENGTH_40, DT(t("max_length_error_dynamic"), [INPUT_LENGTH_40])),

        email: Yup.string().concat(EMAIL_VALIDATION).required("Email is required"),

        phone_number: Yup.string().optional(),

        expertise: Yup.array()
            .of(
                Yup.object({
                    puja_category_id: Yup.number().required("Expertise type is required"),
                })
            )
            .min(1, "At least one expertise is required"),

        language_spoken: Yup.array()
            .of(Yup.string().required("Each language spoken must be a non-empty string"))
            .min(1, "At least one language must be provided")
            .required("Language spoken is required"),

        preferred_language: Yup.string().optional(),

        experience: Yup.number()
            .typeError("Experience must be a number")
            .min(0, "Experience cannot be negative")
            .max(100, "Experience seems too high can not exceed 100")
            .required("Experience is required"),

        education: Yup.string()
            .trim()
            .max(100, "Education cannot exceed 100 characters")
            .required("Education is required"),

        past_consultations: Yup.number()
            .typeError("Past consultations must be a number")
            .integer("Past consultations must be a whole number")
            .min(0, "Past consultations cannot be negative")
            .max(10000000, "Past consultations cannot exceed 10,000,000"),

        gender: Yup.string().required("Gender is required"),
        date_of_birth: Yup.date().required("Date of birth is required").max(new Date(), "Date of birth cannot be in the future"),
        promotional_video: Yup.mixed()
            .nullable()
            .test("fileSize", "Video size is too large (max 50MB)", (value) => {
                if (!value || typeof value === "string") return true;
                return value.size <= 50 * 1024 * 1024;
            }),
        gov_id_proof: Yup.mixed()
            .nullable()
            .test("fileSize", "File size is too large (max 5MB)", (value) => {
                if (!value || typeof value === "string") return true;
                return value.size <= 5 * 1024 * 1024;
            }),
        astrology_certifications: Yup.mixed()
            .nullable()
            .test("fileSize", "File size is too large (max 5MB)", (value) => {
                if (!value || typeof value === "string") return true;
                return value.size <= 5 * 1024 * 1024;
            }),
        cancelled_cheque: Yup.mixed()
            .nullable()
            .test("fileSize", "File size is too large (max 5MB)", (value) => {
                if (!value || typeof value === "string") return true;
                return value.size <= 5 * 1024 * 1024;
            }),

        about_me: Yup.string()
            .trim()
            .optional(),
        profile_image: Yup.mixed()
            .required("Image is required")
            .nullable()
            .test("fileOrUrl", "supported_file_error", (value) => {
                if (!value) return true;
                if (typeof value === "string") return /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm.test(value);
                return ["image/jpg", "image/jpeg", "image/png", "image/webp", "image/gif"].includes(value.type);
            })
            .test("fileSize", "Image size is too large (max 5MB)", (value) => {
                if (!value || typeof value === "string") return true;
                return value.size <= 5 * 1024 * 1024;
            }),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: showdefault && showdefault.name ? showdefault.name : "",
            screen_name: showdefault && showdefault.screen_name ? showdefault.screen_name : "",
            email: showdefault && showdefault.email ? showdefault.email : "",
            phone_number: showdefault && showdefault?.phone_number ? showdefault?.phone_number : "",
            expertise: showdefault && showdefault.expertise
                ? showdefault.expertise.map((exp) => ({
                    puja_category_id: exp.puja_category_id,
                    price_per_minute: exp.price_per_minute,
                }))
                : [],
            language_spoken: showdefault
                ? extractLanguageCodes(showdefault.languages || showdefault.language_spoken)
                : [],
            preferred_language: extractLangCode(showdefault?.preferred_language),
            experience: showdefault && showdefault.experience ? showdefault.experience : "",
            education: showdefault && showdefault.education ? showdefault.education : "",
            past_consultations: showdefault && showdefault.past_consultations ? showdefault.past_consultations : 0,
            gender: showdefault && showdefault.gender ? showdefault.gender : "",
            date_of_birth: showdefault && showdefault.date_of_birth ? new Date(showdefault.date_of_birth) : null,
            promotional_video: showdefault && showdefault.promotional_video ? showdefault.promotional_video : "",
            gov_id_proof: showdefault && showdefault.id_proof ? showdefault.id_proof : "",
            astrology_certifications: showdefault && showdefault.certifications ? showdefault.certifications : "",
            cancelled_cheque: showdefault && showdefault.bank_cheque ? showdefault.bank_cheque : "",
            profile_image: showdefault && showdefault.profile_image ? showdefault.profile_image : "",
            sort_order: showdefault && showdefault.sort_order ? showdefault.sort_order : 0,
            about_me: showdefault && showdefault.about_me ? showdefault.about_me : "",
            is_subscription_astrologer: showdefault && (showdefault.is_subscription_astrologer === true || showdefault.is_subscription_astrologer === "true" || showdefault.is_subscription_astrologer === 1) ? "true" : "false",
            system: extractSystemValues(showdefault?.system),
            chart_type: showdefault && (showdefault.chart_type || showdefault.chart) ? (showdefault.chart_type || showdefault.chart) : "",
            service_type: showdefault && showdefault.service_type ? showdefault.service_type : "both",
        },
        validationSchema: validationSchema,
        onSubmit: (values, { setSubmitting, resetForm }) => {
            globalLoader(true);
            let formData = new FormData();
            formData.append("o_id", params.id);
            const formattedName = values.name ? capitalizeFirstLetterOfEachWord(values.name) : values.name;
            const formattedScreenName = values.screen_name ? capitalizeFirstLetterOfEachWord(values.screen_name) : values.screen_name;
            formData.append("name", formattedName);
            formData.append("screen_name", formattedScreenName);
            formData.append("email", values.email);
            formData.append("phone_number", values.phone_number);
            formData.append("expertise", JSON.stringify(values.expertise));
            formData.append("experience", values.experience);
            formData.append("education", values.education);
            formData.append("past_consultations", values.past_consultations === "" ? 0 : values.past_consultations);
            formData.append("gender", values.gender);
            formData.append("date_of_birth", values.date_of_birth ? new Date(values.date_of_birth).toISOString().split('T')[0] : "");
            formData.append("language_spoken", JSON.stringify(values.language_spoken));
            formData.append("preferred_language", extractLangCode(values.preferred_language));
            if (values.profile_image) formData.append("profile_image", values.profile_image);
            if (values.promotional_video) formData.append("promotional_video", values.promotional_video);
            if (values.gov_id_proof) formData.append("gov_id_proof", values.gov_id_proof);
            if (values.astrology_certifications) formData.append("astrology_certifications", values.astrology_certifications);
            if (values.cancelled_cheque) formData.append("cancelled_cheque", values.cancelled_cheque);
            formData.append("sort_order", values.sort_order);
            formData.append("about_me", values.about_me || "");
            formData.append("is_subscription_astrologer", values.is_subscription_astrologer);
            formData.append("system", JSON.stringify(values.system));
            formData.append("chart_type", values.chart_type);
            formData.append("service_type", values.service_type);
            Edit(formData)
                .then((response) => {
                    setSubmitting(false);
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        if (saveType !== "Save") {
                            navigate(`/admin/astrologer-management/astrologer/list/1`);
                        }
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                    }
                    globalLoader(false);
                })
                .catch((error) => {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(error),
                        ...SWAL_SETTINGS,
                    });
                    console.log("error ====> ", error);
                });
        },
    });



    const addPuja = () => {
        formik.setFieldValue("online_puja", [...formik.values.online_puja, { puja_type: "", price_per_minute: "" }]);
    };

    // Handle removing a puja by its index
    const removePuja = (index) => {
        const currentPujas = formik.values.online_puja;
        if (currentPujas.length > 1) {
            const updatedPujas = currentPujas.filter((_, i) => i !== index);
            formik.setFieldValue("online_puja", updatedPujas);
        } else {
            // Do nothing or reset the last one to empty if removal attempted
            formik.setFieldValue("online_puja", [{ puja_type: "", price_per_minute: "" }]);
        }
    };

    console.log("formik.values", formik.values);
    console.log("formik.errors", formik.errors);

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <form onSubmit={formik.handleSubmit}>
                <div className="row row-sm">
                    <div className="col-lg-12 col-md-12 animation_fade">
                        <div className="card custom-card">
                            <div className="card-body">
                                <div>
                                    <h6 className="main-content-label mb-3">{t("Edit Astrologer")}</h6>
                                </div>
                                <div className="row row-sm">
                                    <div className="col-lg-6 form-group">
                                        <label htmlFor="profile_image" className="text-left d-flex">
                                            {t("label_logo")}:{" "}
                                            <Whisper placement="top" controlId="control-id-hover" trigger="hover" speaker={<Tooltip>{t("image_support_tooltip")}</Tooltip>}>
                                                <span className="field-more-info mt-1 ms-1 cp">?</span>
                                            </Whisper>
                                        </label>
                                        <input
                                            className="form-control imgInput"
                                            id="profile_image"
                                            name="profile_image"
                                            accept="image/*"
                                            type="file"
                                            onChange={(event) => {
                                                formik.setFieldValue("profile_image", event.target.files[0] || "");
                                                event.target.files.length === 1 ? setPreviewImage(URL.createObjectURL(event.target.files[0])) : setPreviewImage("");
                                            }}
                                        />
                                        <span className="text-danger d-flex text-left">
                                            <CustomError name="profile_image" form={formik} />
                                        </span>
                                        {previewimage ? (
                                            <ul className="question-image-preview questions-ul">
                                                <li className="pr_img_box">
                                                    <img src={previewimage} style={{ height: "100px", borderRadius: "8px" }} alt={"profileImg"} />
                                                </li>
                                            </ul>
                                        ) : (
                                            ""
                                        )}
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="name" className="text-left d-flex">
                                            {t("Name")}:<span className="requirestar">*</span>{" "}
                                        </label>
                                        <input
                                            name="name"
                                            id="name"
                                            type="text"
                                            onChange={formik.handleChange}
                                            onBlur={(e) => {
                                                formik.handleBlur(e);
                                                if (e.target.value) {
                                                    formik.setFieldValue("name", capitalizeFirstLetterOfEachWord(e.target.value));
                                                }
                                            }}
                                            value={formik.values.name}
                                            className="form-control"
                                            placeholder={t("Enter name")}
                                        />
                                        <span className="text-danger d-flex text-left">
                                            <CustomError name="name" form={formik} />
                                        </span>
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="screen_name" className="text-left d-flex">
                                            {t("Screen Name")}:
                                        </label>
                                        <input
                                            name="screen_name"
                                            id="screen_name"
                                            type="text"
                                            onChange={formik.handleChange}
                                            onBlur={(e) => {
                                                formik.handleBlur(e);
                                                if (e.target.value) {
                                                    formik.setFieldValue("screen_name", capitalizeFirstLetterOfEachWord(e.target.value));
                                                }
                                            }}
                                            value={formik.values.screen_name}
                                            className="form-control"
                                            placeholder={t("Enter screen name")}
                                        />
                                        <span className="text-danger d-flex text-left">
                                            <CustomError name="screen_name" form={formik} />
                                        </span>
                                    </div>

                                    {/* Sort Order */}
                                    <div className="col-md-6 form-group">
                                        <label htmlFor="sort_order" className="text-left d-flex">
                                            Sort Order / Priority :
                                        </label>
                                        <input
                                            name="sort_order"
                                            id="sort_order"
                                            type="number"
                                            min="0"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.sort_order}
                                            className="form-control"
                                            placeholder="Enter sort order (e.g. 1, 2, 3)"
                                        />
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="is_subscription_astrologer" className="text-left d-flex">
                                            {t("Is Subscription Astrologer")}:
                                        </label>
                                        <select
                                            name="is_subscription_astrologer"
                                            id="is_subscription_astrologer"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.is_subscription_astrologer}
                                            className="form-control"
                                        >
                                            <option value="false">{t("No")}</option>
                                            <option value="true">{t("Yes")}</option>
                                        </select>
                                        <CustomError name="is_subscription_astrologer" form={formik} />
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="system" className="text-left d-flex">
                                            {t("System")}:
                                        </label>
                                        <TagPicker
                                            data={systemData}
                                            block
                                            value={formik.values.system}
                                            onChange={(value) => formik.setFieldValue("system", value || [])}
                                            placeholder={t("Select System")}
                                        />
                                        <CustomError name="system" form={formik} />
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="chart_type" className="text-left d-flex">
                                            {t("Chart")}:
                                        </label>
                                        <select
                                            name="chart_type"
                                            id="chart_type"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.chart_type}
                                            className="form-control"
                                        >
                                            <option value="">{t("Select Chart")}</option>
                                            <option value="SINGLE">{t("SINGLE")}</option>
                                            <option value="DUAL">{t("DUAL")}</option>
                                        </select>
                                        <CustomError name="chart_type" form={formik} />
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="service_type" className="text-left d-flex">
                                            {t("Service Provided")}:<span className="requirestar">*</span>
                                        </label>
                                        <select
                                            name="service_type"
                                            id="service_type"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.service_type}
                                            className="form-control"
                                        >
                                            <option value="both">{t("Both (Chat & Call)")}</option>
                                            <option value="chat">{t("Only Chat")}</option>
                                            <option value="call">{t("Only Call")}</option>
                                            <option value="video_call">{t("Only Video Call")}</option>
                                            <option value="chat_video">{t("Chat & Video Call")}</option>
                                            <option value="call_video">{t("Call & Video Call")}</option>
                                            <option value="all">{t("All (Chat, Call & Video Call)")}</option>
                                        </select>
                                        <CustomError name="service_type" form={formik} />
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="about_me" className="text-left d-flex">
                                            {t("About Me")}:
                                        </label>
                                        <textarea
                                            name="about_me"
                                            id="about_me"
                                            rows="2"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.about_me}
                                            className="form-control"
                                            placeholder={t("Enter about me details")}
                                        />
                                        <span className="text-danger d-flex text-left">
                                            <CustomError name="about_me" form={formik} />
                                        </span>
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="gender" className="text-left d-flex">
                                            {t("Gender")}:<span className="requirestar">*</span>
                                        </label>
                                        <select
                                            name="gender"
                                            id="gender"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.gender}
                                            className="form-control"
                                        >
                                            <option value="">Select Gender</option>
                                            {GENDER.map((gender, i) => (
                                                <option key={i} value={gender}>{t(gender)}</option>
                                            ))}
                                        </select>
                                        <CustomError name="gender" form={formik} />
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="date_of_birth" className="text-left d-flex">
                                            {t("Date of Birth")}:<span className="requirestar">*</span>
                                        </label>
                                        <DatePicker
                                            oneTap
                                            block
                                            format="dd/MM/yyyy"
                                            value={formik.values.date_of_birth}
                                            onChange={(value) => formik.setFieldValue("date_of_birth", value)}
                                            onBlur={() => formik.setFieldTouched("date_of_birth", true)}
                                            placeholder="Select Date of Birth"
                                            shouldDisableDate={(date) => date > new Date()}
                                        />
                                        <CustomError name="date_of_birth" form={formik} />
                                    </div>

                                    <div className="col-lg-6 form-group">
                                        <label htmlFor="email" className="text-left d-flex">
                                            {t("label_email")}:<span className="requirestar">*</span>{" "}
                                        </label>
                                        <input
                                            name="email"
                                            id="email"
                                            type="email"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.email}
                                            className="form-control"
                                            placeholder={t("placeholder_email")}
                                        />
                                        <span className="text-danger d-flex text-left">
                                            <CustomError name="email" form={formik} />
                                        </span>
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label className="text-left d-flex" htmlFor="phone_number">
                                            {t("label_phone_number")}:<span className="requirestar">*</span>{" "}
                                        </label>
                                        <input
                                            name="phone_number"
                                            id="phone_number"
                                            type="text"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.phone_number}
                                            className="form-control"
                                            placeholder={t("Enter phone number")}
                                            disabled
                                        />
                                        <span className="text-danger d-flex text-left">
                                            <CustomError name="phone_number" form={formik} />
                                        </span>
                                    </div>

                                    <div className="col-lg-6 form-group">
                                        <label htmlFor="promotional_video" className="text-left d-flex">
                                            {t("Promotional Video")}:
                                        </label>
                                        <input
                                            className="form-control"
                                            id="promotional_video"
                                            name="promotional_video"
                                            accept="video/*"
                                            type="file"
                                            onChange={(event) => {
                                                formik.setFieldValue("promotional_video", event.currentTarget.files[0]);
                                            }}
                                        />
                                        <CustomError name="promotional_video" form={formik} />
                                        {formik.values.promotional_video && typeof formik.values.promotional_video === "string" && (
                                            <div className="mt-2 text-muted small">Current Video: {formik.values.promotional_video.split('/').pop()}</div>
                                        )}
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="language_spoken" className="text-left d-flex">
                                            {t("Language Spoken")}:<span className="requirestar">*</span>
                                        </label>
                                        <TagPicker
                                            data={languageSpoken.map(lang => ({ label: lang.name, value: lang.code }))}
                                            block
                                            value={formik.values.language_spoken}
                                            onChange={(value) => formik.setFieldValue('language_spoken', value || [])}
                                            placeholder="Select one or more languages"
                                        />
                                        <CustomError name="language_spoken" form={formik} />
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="preferred_language" className="text-left d-flex">
                                            {t("Preferred Language")}:
                                        </label>
                                        <select
                                            name="preferred_language"
                                            id="preferred_language"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={extractLangCode(formik.values.preferred_language)}
                                            className="form-control"
                                        >
                                            <option value="">{t("Select Preferred Language")}</option>
                                            {languageSpoken.map((lang, i) => (
                                                <option key={i} value={lang.code}>
                                                    {lang.name}
                                                </option>
                                            ))}
                                        </select>
                                        <CustomError name="preferred_language" form={formik} />
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="experience" className="text-left d-flex">
                                            {t("Experience (Year) ")}:<span className="requirestar">*</span>{" "}
                                        </label>
                                        <div className="input-group">
                                            <input
                                                name="experience"
                                                id="experience"
                                                type="number"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.experience}
                                                className="form-control"
                                                placeholder={t("Enter experience")}
                                            />
                                            <span className="input-group-text">Year</span>
                                        </div>
                                        <span className="text-danger d-flex text-left">
                                            <CustomError name="experience" form={formik} />
                                        </span>
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="education" className="text-left d-flex">
                                            {t("Education")}:<span className="requirestar">*</span>
                                        </label>
                                        <input
                                            name="education"
                                            id="education"
                                            type="text"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.education}
                                            className="form-control"
                                            placeholder={t("Enter education (e.g. B.A. in Astrology)")}
                                        />
                                        <CustomError name="education" form={formik} />
                                    </div>

                                    <div className="col-md-12 form-group">
                                        <label htmlFor="past_consultations" className="text-left d-flex">
                                            {t("Past Consultations (Verified)")}:
                                        </label>
                                        <div className="input-group">
                                            <input
                                                name="past_consultations"
                                                id="past_consultations"
                                                type="number"
                                                min="0"
                                                step="1"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                onKeyDown={blockInvalidChar}
                                                value={formik.values.past_consultations}
                                                className="form-control"
                                                placeholder={t("Enter past consultations")}
                                            />
                                            <span className="input-group-text">Consultations</span>
                                        </div>
                                        <small className="text-muted d-flex text-left mt-1">
                                            Before joining the app. App sessions are added automatically.
                                        </small>
                                        <span className="text-danger d-flex text-left">
                                            <CustomError name="past_consultations" form={formik} />
                                        </span>
                                    </div>

                                     <div className="col-md-12">
                                        <hr />
                                        <h6>{t("Expertise")}</h6>
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <label htmlFor="expertise" className="text-left d-flex">
                                            {t("Select Expertise")}:<span className="requirestar">*</span>
                                        </label>
                                        <TagPicker
                                            data={categories.map(cat => ({ label: cat.title, value: cat.id }))}
                                            block
                                            value={formik.values.expertise.map(e => Number(e.puja_category_id))}
                                            onChange={(selectedValues) => {
                                                const updatedExpertise = selectedValues.map(val => {
                                                    return { puja_category_id: val, price_per_minute: 0 };
                                                });
                                                formik.setFieldValue('expertise', updatedExpertise);
                                            }}
                                            onBlur={formik.handleBlur}
                                            placeholder="Select one or more expertise"
                                        />
                                        {formik.touched.expertise && typeof formik.errors.expertise === 'string' ? (
                                            <div className="text-danger d-flex text-left">{formik.errors.expertise}</div>
                                        ) : null}
                                    </div>
                                </div>


                                <div className="row row-sm">
                                    <div className="col-md-12">
                                        <hr />
                                        <h6>{t("KYC Verification")}</h6>
                                    </div>

                                    <div className="col-lg-4 form-group">
                                        <label htmlFor="gov_id_proof" className="text-left d-flex">
                                            {t("Government ID Proof")}:
                                        </label>
                                        <input
                                            className="form-control"
                                            id="gov_id_proof"
                                            name="gov_id_proof"
                                            type="file"
                                            disabled
                                            onChange={(event) => {
                                                formik.setFieldValue("gov_id_proof", event.currentTarget.files[0]);
                                            }}
                                        />
                                        <CustomError name="gov_id_proof" form={formik} />
                                        {formik.values.gov_id_proof && typeof formik.values.gov_id_proof === "string" && (
                                            <div className="mt-1 small text-info">Current: [View Document]({formik.values.gov_id_proof})</div>
                                        )}
                                    </div>

                                    <div className="col-lg-4 form-group">
                                        <label htmlFor="astrology_certifications" className="text-left d-flex">
                                            {t("Astrology Certifications")}:
                                        </label>
                                        <input
                                            className="form-control"
                                            id="astrology_certifications"
                                            name="astrology_certifications"
                                            type="file"
                                            disabled
                                            onChange={(event) => {
                                                formik.setFieldValue("astrology_certifications", event.currentTarget.files[0]);
                                            }}
                                        />
                                        <CustomError name="astrology_certifications" form={formik} />
                                        {formik.values.astrology_certifications && typeof formik.values.astrology_certifications === "string" && (
                                            <div className="mt-1 small text-info">Current: [View Document]({formik.values.astrology_certifications})</div>
                                        )}
                                    </div>

                                    <div className="col-lg-4 form-group">
                                        <label htmlFor="cancelled_cheque" className="text-left d-flex">
                                            {t("Cancelled Bank Cheque")}:
                                        </label>
                                        <input
                                            className="form-control"
                                            id="cancelled_cheque"
                                            name="cancelled_cheque"
                                            type="file"
                                            disabled
                                            onChange={(event) => {
                                                formik.setFieldValue("cancelled_cheque", event.currentTarget.files[0]);
                                            }}
                                        />
                                        <CustomError name="cancelled_cheque" form={formik} />
                                        {formik.values.cancelled_cheque && typeof formik.values.cancelled_cheque === "string" && (
                                            <div className="mt-1 small text-info">Current: [View Document]({formik.values.cancelled_cheque})</div>
                                        )}
                                    </div>
                                    <div className="mt-5">
                                        <button
                                            onClick={() => {
                                                setSaveType("Save");
                                            }}
                                            className="btn btn-info mr-2"
                                            type="submit"
                                        >
                                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                            {t("btn_save")}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSaveType("");
                                            }}
                                            className="btn btn-success mr-2"
                                            type="submit"
                                        >
                                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                            {t("btn_save_exit")}
                                        </button>
                                        <button className="btn ripple btn-secondary" type="button" onClick={() => navigate(-1)}>
                                            <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                            {t("btn_cancel")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
};

export default AstrologerEdit;
